import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { BulkCompleteResult, BulkCompleteFailReason } from '@vagusx/clients';

/**
 * Reads out the per-row failure reasons from a partial-success bulk
 * operation. Opened from the warning toast's `Show details` action.
 *
 * Reason copy is human-friendly — server reasons are short codes
 * (`not_found`, `error`) that aren't meant for the user's eyes.
 *
 * Caller passes a `titleById` lookup so each failed row shows its
 * task title (mono UUID is unhelpful to operators). For `not_found`
 * the title may be stale because the row was deleted between
 * selection and the API call — in that case the reason copy
 * ("Task no longer exists") makes the staleness honest.
 */
interface BulkFailureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  failed: BulkCompleteResult['failed'];
  titleById: Map<string, string>;
}

const REASON_COPY: Record<BulkCompleteFailReason, string> = {
  not_found: "Task no longer exists or you don't have access to it.",
  error: 'A temporary error happened — try again in a moment.',
};

export function BulkFailureDialog({
  open,
  onOpenChange,
  failed,
  titleById,
}: BulkFailureDialogProps): React.ReactElement {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {failed.length} {failed.length === 1 ? 'task' : 'tasks'} couldn't be marked done
          </DialogTitle>
          <DialogDescription>
            The other tasks were updated successfully. These weren't:
          </DialogDescription>
        </DialogHeader>

        <ul className="space-y-2 max-h-72 overflow-y-auto text-sm">
          {failed.map((f) => {
            const title = titleById.get(f.id);
            return (
              <li key={f.id} className="rounded-md border bg-muted/30 px-3 py-2">
                <div className="truncate font-medium">{title ?? 'Unknown task'}</div>
                <div className="text-muted-foreground">{REASON_COPY[f.reason]}</div>
              </li>
            );
          })}
        </ul>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
